import type { PackageComponent } from '@utils/skip-package-component';
import {
  LEAGUE_SEO_ROUTE_REGISTRY,
  getLeagueRouteConfig,
  isLeaguePathActive,
} from '@utils/navigation/league-public-paths';

const PENDING_CONVERSIONS_KEY = 'ga4_pending_conversions';
const PENDING_CONVERSION_TTL_MS = 2 * 60 * 60 * 1000;

export type BookingConversionEvent = 'purchase' | 'paid_partial_booking';
export type Ga4PackageComponent = PackageComponent;
export type Ga4RemovalReason = 'skipped' | 'removed' | 'replaced';

export type Ga4Item = {
  item_id: string;
  item_name?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_list_id?: string;
  item_list_name?: string;
  index?: number;
  coupon?: string;
  discount?: number;
  item_brand?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
};

export type Ga4PageViewContext = {
  page_type: string;
  page_name?: string;
  league?: string;
  team_slug?: string;
  package_type?: 'package' | 'tickets_only';
};

export type BookingConversion = {
  eventName: BookingConversionEvent;
  transactionId: string;
  params: {
    transaction_id: string;
    value: number;
    currency: 'CAD' | 'USD';
    failure_stage?: string;
    items: Ga4Item[];
  };
};

type StoredConversion = BookingConversion & {
  expiresAt: number;
  sentAt?: number;
};

type StoredConversions = Record<string, StoredConversion>;

type AnalyticsWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  ga4InitializedFor?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isOptionalFiniteNumber = (value: unknown) =>
  value === undefined || (typeof value === 'number' && Number.isFinite(value));

const isGa4Item = (value: unknown): value is Ga4Item => {
  if (!isRecord(value) || typeof value.item_id !== 'string') return false;

  return (
    (value.item_name === undefined || typeof value.item_name === 'string') &&
    (value.item_category === undefined ||
      typeof value.item_category === 'string') &&
    (value.item_category2 === undefined ||
      typeof value.item_category2 === 'string') &&
    (value.item_category3 === undefined ||
      typeof value.item_category3 === 'string') &&
    (value.item_list_id === undefined ||
      typeof value.item_list_id === 'string') &&
    (value.item_list_name === undefined ||
      typeof value.item_list_name === 'string') &&
    (value.coupon === undefined || typeof value.coupon === 'string') &&
    (value.item_brand === undefined || typeof value.item_brand === 'string') &&
    (value.item_variant === undefined ||
      typeof value.item_variant === 'string') &&
    isOptionalFiniteNumber(value.index) &&
    isOptionalFiniteNumber(value.discount) &&
    isOptionalFiniteNumber(value.price) &&
    isOptionalFiniteNumber(value.quantity)
  );
};

const isStoredConversion = (
  value: unknown,
  now: number
): value is StoredConversion => {
  if (!isRecord(value) || typeof value.transactionId !== 'string') {
    return false;
  }

  const eventName = value.eventName;
  const params = value.params;
  if (
    (eventName !== 'purchase' && eventName !== 'paid_partial_booking') ||
    !isRecord(params) ||
    params.transaction_id !== value.transactionId ||
    (params.currency !== 'CAD' && params.currency !== 'USD') ||
    typeof params.value !== 'number' ||
    !Number.isFinite(params.value) ||
    !Array.isArray(params.items) ||
    !params.items.length ||
    !params.items.every(isGa4Item) ||
    (params.failure_stage !== undefined &&
      typeof params.failure_stage !== 'string') ||
    typeof value.expiresAt !== 'number' ||
    !Number.isFinite(value.expiresAt) ||
    value.expiresAt <= now ||
    (value.sentAt !== undefined &&
      (typeof value.sentAt !== 'number' || !Number.isFinite(value.sentAt)))
  ) {
    return false;
  }

  return true;
};

const getStorage = () => {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const readConversions = (now = Date.now()): StoredConversions => {
  const storage = getStorage();
  if (!storage) return {};

  try {
    const parsed: unknown = JSON.parse(
      storage.getItem(PENDING_CONVERSIONS_KEY) || '{}'
    );
    if (!isRecord(parsed)) return {};

    const validConversions: StoredConversions = {};
    Object.entries(parsed).forEach(([transactionId, conversion]) => {
      if (
        isStoredConversion(conversion, now) &&
        conversion.transactionId === transactionId
      ) {
        validConversions[transactionId] = conversion;
      }
    });

    return validConversions;
  } catch {
    return {};
  }
};

const writeConversions = (conversions: StoredConversions) => {
  const storage = getStorage();
  if (!storage) return false;

  try {
    if (Object.keys(conversions).length === 0) {
      storage.removeItem(PENDING_CONVERSIONS_KEY);
    } else {
      storage.setItem(PENDING_CONVERSIONS_KEY, JSON.stringify(conversions));
    }
    return true;
  } catch {
    return false;
  }
};

const getMeasurementId = () =>
  process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim();

const GA4_ALLOWED_QUERY_PARAMS = new Set([
  'utm_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'dclid',
  'gbraid',
  'wbraid',
  'mc_cid',
]);

export const shouldTrackGa4Pathname = (pathname: string) =>
  !['/dashboard', '/callback', '/api/auth'].some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

export const normalizeGa4Pathname = (pathname: string) => {
  if (/^\/confirmation\/[^/]+\/?$/.test(pathname)) {
    return '/confirmation/:orderId';
  }
  if (/^\/search-hotel\/[^/]+\/?$/.test(pathname)) {
    return '/search-hotel/:hotelId';
  }
  return pathname;
};

const getGa4Location = () => {
  if (typeof window === 'undefined') return null;

  const url = new URL(window.location.href);
  [...url.searchParams.keys()].forEach((key) => {
    if (!GA4_ALLOWED_QUERY_PARAMS.has(key.toLowerCase())) {
      url.searchParams.delete(key);
    }
  });
  url.pathname = normalizeGa4Pathname(url.pathname);

  return {
    href: url.href,
    path: `${url.pathname}${url.search}`,
  };
};

const getMailchimpCampaignConfig = () => {
  if (typeof window === 'undefined') return {};

  const searchParams = new URLSearchParams(window.location.search);
  const mailchimpCampaignId = searchParams.get('mc_cid')?.trim();
  if (!mailchimpCampaignId) return {};

  const campaignId = searchParams.get('utm_id')?.trim() || mailchimpCampaignId;
  const campaignName = searchParams.get('utm_campaign')?.trim();
  const campaignSource = searchParams.get('utm_source')?.trim();
  const campaignMedium = searchParams.get('utm_medium')?.trim();
  const campaignContent = searchParams.get('utm_content')?.trim();
  const campaignTerm = searchParams.get('utm_term')?.trim();

  return {
    campaign_id: campaignId,
    campaign_name: campaignName || `mailchimp_${mailchimpCampaignId}`,
    campaign_source: campaignSource || 'mailchimp',
    campaign_medium: campaignMedium || 'email',
    ...(campaignContent ? { campaign_content: campaignContent } : {}),
    ...(campaignTerm ? { campaign_term: campaignTerm } : {}),
  };
};

const getGtag = () => {
  if (typeof window === 'undefined') return null;

  const measurementId = getMeasurementId();
  const isTrackingEnabled =
    process.env.NEXT_PUBLIC_USE_META?.toLowerCase() !== 'false';
  if (!measurementId || !isTrackingEnabled) return null;

  const analyticsWindow = window as AnalyticsWindow;
  analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];

  if (!analyticsWindow.gtag) {
    analyticsWindow.gtag = function gtag(..._args: unknown[]) {
      // Google expects the function's Arguments object in dataLayer.
      // eslint-disable-next-line prefer-rest-params
      analyticsWindow.dataLayer?.push(arguments);
    };
  }

  return analyticsWindow.gtag;
};

export const initializeGa4 = () => {
  const measurementId = getMeasurementId();
  const gtag = getGtag();
  if (!measurementId || !gtag) return false;

  const location = getGa4Location();
  const analyticsWindow = window as AnalyticsWindow;
  if (analyticsWindow.ga4InitializedFor === measurementId) {
    if (location) gtag('set', { page_location: location.href });
    return true;
  }

  gtag('js', new Date());
  if (location) gtag('set', { page_location: location.href });
  gtag('config', measurementId, {
    send_page_view: false,
    ...getMailchimpCampaignConfig(),
  });
  analyticsWindow.ga4InitializedFor = measurementId;
  return true;
};

export const trackGa4Event = (
  eventName: string,
  params: Record<string, unknown> = {}
) => {
  const gtag = getGtag();
  if (!gtag) return false;

  initializeGa4();
  gtag('event', eventName, params);

  const analyticsWindow = window as AnalyticsWindow;
  if (analyticsWindow.dataLayer) {
    analyticsWindow.dataLayer.push({
      event: eventName,
      ...params,
    });
  }

  return true;
};

export const getGa4PageViewContext = (
  pathname: string,
  search = ''
): Ga4PageViewContext => {
  const segments = pathname.split('/').filter(Boolean);
  const packageType = new URLSearchParams(search).get('packageType');
  const package_type =
    packageType === 'packages'
      ? 'package'
      : packageType === 'tickets'
        ? 'tickets_only'
        : undefined;
  const formatSegment = (segment?: string) =>
    segment
      ? segment
          .replace(/[-_]+/g, ' ')
          .replace(/\b\w/g, (character) => character.toUpperCase())
      : '';
  const formatLeagueName = (segment?: string) => {
    const normalized = normalizeSportCategory(segment);
    return normalized === 'Other' ? formatSegment(segment) : normalized;
  };

  if (pathname === '/') return { page_type: 'home', page_name: 'Home' };

  const vanityLeague = Object.keys(LEAGUE_SEO_ROUTE_REGISTRY).find((league) =>
    isLeaguePathActive(pathname, league)
  );
  if (vanityLeague) {
    const { publicLandingPath, publicTeamBasePath } =
      getLeagueRouteConfig(vanityLeague);
    const isLandingPage = pathname.replace(/\/$/, '') === publicLandingPath;
    const teamSlug = isLandingPage
      ? undefined
      : pathname
          .replace(`${publicTeamBasePath}/`, '')
          .split('/')
          .filter(Boolean)[0];
    const leagueName = formatLeagueName(vanityLeague);

    return {
      page_type: teamSlug ? 'team' : 'league',
      page_name: teamSlug
        ? `${formatSegment(teamSlug)} - ${leagueName}`
        : `${leagueName} Events`,
      league: vanityLeague,
      ...(teamSlug ? { team_slug: teamSlug } : {}),
      ...(package_type ? { package_type } : {}),
    };
  }

  if (pathname === '/european-soccer-travel-packages') {
    return {
      page_type: 'league',
      page_name: 'European Soccer Events',
      league: 'european-soccer',
    };
  }

  if (segments[0] === 'events') {
    const leagueName = formatLeagueName(segments[1]);
    const teamName = formatSegment(segments[2]);
    return {
      page_type: segments[2] ? 'team' : 'league',
      page_name: segments[2]
        ? `${teamName} - ${leagueName}`
        : `${leagueName} Events`,
      league: segments[1],
      ...(segments[2] ? { team_slug: segments[2] } : {}),
      ...(package_type ? { package_type } : {}),
    };
  }

  if (segments[0] === 'city')
    return {
      page_type: 'city',
      page_name: segments[1]
        ? `Events in ${formatSegment(segments[1])}`
        : 'Events by City',
    };
  if (segments[0] === 'concerts')
    return { page_type: 'concerts', page_name: 'Concerts' };
  if (segments[0] === 'buy')
    return {
      page_type: 'buy',
      page_name: 'Package Selection',
      ...(package_type ? { package_type } : {}),
    };
  if (segments[0] === 'checkout')
    return {
      page_type: 'checkout',
      page_name: 'Checkout',
      ...(package_type ? { package_type } : {}),
    };
  if (segments[0] === 'confirmation')
    return { page_type: 'confirmation', page_name: 'Booking Confirmation' };
  if (segments[0] === 'search-tickets')
    return {
      page_type: 'search_tickets',
      page_name: 'Ticket Search',
      ...(package_type ? { package_type } : {}),
    };
  if (segments[0] === 'search-flights')
    return {
      page_type: 'search_flights',
      page_name: 'Flight Search',
      ...(package_type ? { package_type } : {}),
    };
  if (segments[0] === 'search-hotel') {
    return {
      page_type: segments[1] ? 'hotel_detail' : 'search_hotel',
      page_name: segments[1] ? 'Hotel Details' : 'Hotel Search',
      ...(package_type ? { package_type } : {}),
    };
  }

  return {
    page_type: 'other',
    page_name: formatSegment(segments[0]) || 'Other',
  };
};

const GENERIC_SITE_TITLE =
  'Sports Travel Packages, Tickets & Hotels | Elite Sports Tours';

export const trackGa4PageView = (context: Partial<Ga4PageViewContext> = {}) => {
  if (!shouldTrackGa4Pathname(window.location.pathname)) return false;
  const documentTitle = document.title.trim();
  const hasGenericTitle =
    !documentTitle || documentTitle === GENERIC_SITE_TITLE;
  const pageTitle =
    hasGenericTitle && context.page_type !== 'home' && context.page_name
      ? context.page_name
      : documentTitle;

  const location = getGa4Location();

  return trackGa4Event('page_view', {
    page_location: location?.href ?? window.location.href,
    page_path:
      location?.path ?? `${window.location.pathname}${window.location.search}`,
    page_title: pageTitle,
    ...context,
  });
};

const addListContext = (
  items: Ga4Item[],
  itemListId: string,
  itemListName: string
) =>
  items.map((item) => ({
    ...item,
    item_list_id: item.item_list_id ?? itemListId,
    item_list_name: item.item_list_name ?? itemListName,
  }));

const MAX_GA4_ITEM_LIST_ITEMS = 25;

export const trackGa4ItemListView = ({
  itemListId,
  itemListName,
  items,
}: {
  itemListId: string;
  itemListName: string;
  items: Ga4Item[];
}) => {
  if (!items.length) return false;

  const trackedItems = items.slice(0, MAX_GA4_ITEM_LIST_ITEMS);

  return trackGa4Event('view_item_list', {
    item_list_id: itemListId,
    item_list_name: itemListName,
    ...(trackedItems.length < items.length
      ? { total_item_count: items.length }
      : {}),
    items: addListContext(trackedItems, itemListId, itemListName),
  });
};

export const trackGa4ItemSelection = ({
  itemListId,
  itemListName,
  item,
}: {
  itemListId: string;
  itemListName: string;
  item: Ga4Item;
}) =>
  trackGa4Event('select_item', {
    item_list_id: itemListId,
    item_list_name: itemListName,
    items: addListContext([item], itemListId, itemListName),
  });

const getEcommerceParams = ({
  currency,
  items,
  value,
  params = {},
}: {
  currency?: string | null;
  items: Ga4Item[];
  value?: number;
  params?: Record<string, unknown>;
}) => {
  const normalizedCurrency = normalizeGa4Currency(currency);
  const normalizedValue = Number(value);
  const calculatedValue =
    Number.isFinite(normalizedValue) && normalizedValue > 0
      ? Math.round(normalizedValue * 100) / 100
      : items.reduce(
          (sum, it) =>
            sum + (Number(it.price) || 0) * (Number(it.quantity) || 1),
          0
        );
  const finalValue =
    calculatedValue > 0
      ? Math.round(calculatedValue * 100) / 100
      : Number.isFinite(normalizedValue) && normalizedValue >= 0
        ? normalizedValue
        : undefined;

  return {
    ...params,
    ...(normalizedCurrency ? { currency: normalizedCurrency } : {}),
    ...(finalValue !== undefined ? { value: finalValue } : {}),
    items,
  };
};

export const trackGa4ItemView = ({
  currency,
  item,
  value,
  params,
}: {
  currency?: string | null;
  item: Ga4Item;
  value?: number;
  params?: Record<string, unknown>;
}) =>
  trackGa4Event(
    'view_item',
    getEcommerceParams({ currency, items: [item], value, params })
  );

export const trackGa4CartView = ({
  currency,
  items,
  value,
  params,
}: {
  currency?: string | null;
  items: Ga4Item[];
  value?: number;
  params?: Record<string, unknown>;
}) => {
  if (!items.length) return false;
  return trackGa4Event(
    'view_cart',
    getEcommerceParams({ currency, items, value, params })
  );
};

const normalizeGa4Currency = (currency?: string | null) => {
  const normalized = currency?.trim().toUpperCase();
  return normalized === 'CAD' || normalized === 'USD' ? normalized : undefined;
};

export const trackGa4AddToCart = ({
  currency,
  item,
  value,
  params = {},
}: {
  currency?: string | null;
  item: Ga4Item;
  value?: number;
  params?: Record<string, unknown>;
}) => {
  const normalizedCurrency = normalizeGa4Currency(currency);
  const normalizedValue = Number(value);
  const calculatedValue =
    Number.isFinite(normalizedValue) && normalizedValue > 0
      ? Math.round(normalizedValue * 100) / 100
      : (Number(item.price) || 0) * (Number(item.quantity) || 1);
  const finalValue =
    calculatedValue > 0
      ? Math.round(calculatedValue * 100) / 100
      : Number.isFinite(normalizedValue) && normalizedValue >= 0
        ? normalizedValue
        : undefined;

  return trackGa4Event('add_to_cart', {
    ...params,
    ...(normalizedCurrency ? { currency: normalizedCurrency } : {}),
    ...(finalValue !== undefined ? { value: finalValue } : {}),
    items: [item],
  });
};

export const trackGa4PackageComponentRemoved = ({
  component,
  currency,
  item,
  reason = 'skipped',
  value,
  params = {},
}: {
  component: Ga4PackageComponent;
  currency?: string | null;
  item?: Ga4Item;
  reason?: Ga4RemovalReason;
  value?: number;
  params?: Record<string, unknown>;
}) => {
  const normalizedCurrency = normalizeGa4Currency(currency);
  const normalizedValue = Number(value);
  const fallbackItem: Ga4Item = {
    item_id: `${component}:selected`,
    item_name: `Selected ${component}`,
    item_category: component === 'flights' ? 'flight' : component,
    quantity: 1,
  };
  const targetItem = item ?? fallbackItem;
  const calculatedValue =
    Number.isFinite(normalizedValue) && normalizedValue > 0
      ? Math.round(normalizedValue * 100) / 100
      : (Number(targetItem.price) || 0) * (Number(targetItem.quantity) || 1);
  const finalValue =
    calculatedValue > 0
      ? Math.round(calculatedValue * 100) / 100
      : Number.isFinite(normalizedValue) && normalizedValue >= 0
        ? normalizedValue
        : undefined;

  return trackGa4Event('remove_from_cart', {
    ...params,
    package_component: component,
    removal_reason: reason,
    ...(normalizedCurrency ? { currency: normalizedCurrency } : {}),
    ...(finalValue !== undefined ? { value: finalValue } : {}),
    items: [targetItem],
  });
};

export const trackGa4BeginCheckout = ({
  currency,
  items,
  value,
  params = {},
}: {
  currency?: string | null;
  items: Ga4Item[];
  value?: number;
  params?: Record<string, unknown>;
}) => {
  if (!items.length) return false;

  const normalizedCurrency = normalizeGa4Currency(currency);
  const normalizedValue = Number(value);

  return trackGa4Event('begin_checkout', {
    ...params,
    ...(normalizedCurrency ? { currency: normalizedCurrency } : {}),
    ...(Number.isFinite(normalizedValue) && normalizedValue > 0
      ? { value: Math.round(normalizedValue * 100) / 100 }
      : {}),
    items,
  });
};

export const trackGa4AddPaymentInfo = ({
  currency,
  items,
  paymentType,
  value,
  params = {},
}: {
  currency?: string | null;
  items: Ga4Item[];
  paymentType?: string;
  value?: number;
  params?: Record<string, unknown>;
}) => {
  if (!items.length) return false;

  const normalizedCurrency = normalizeGa4Currency(currency);
  const normalizedValue = Number(value);

  return trackGa4Event('add_payment_info', {
    ...params,
    ...(paymentType ? { payment_type: paymentType } : {}),
    ...(normalizedCurrency ? { currency: normalizedCurrency } : {}),
    ...(Number.isFinite(normalizedValue) && normalizedValue > 0
      ? { value: Math.round(normalizedValue * 100) / 100 }
      : {}),
    items,
  });
};

export const trackGa4AddShippingInfo = ({
  currency,
  items,
  shippingTier = 'travel_documents',
  value,
  params = {},
}: {
  currency?: string | null;
  items: Ga4Item[];
  shippingTier?: string;
  value?: number;
  params?: Record<string, unknown>;
}) => {
  if (!items.length) return false;

  return trackGa4Event(
    'add_shipping_info',
    getEcommerceParams({
      currency,
      items,
      value,
      params: { ...params, shipping_tier: shippingTier },
    })
  );
};

export const trackGa4GenerateLead = ({
  formName,
  params = {},
}: {
  formName: string;
  params?: Record<string, unknown>;
}) => trackGa4Event('generate_lead', { ...params, form_name: formName });

const getSafeSearchTerm = (term: string) => {
  const normalized = term.trim().replace(/\s+/g, ' ').slice(0, 100);
  const looksLikeEmail = /\S+@\S+\.\S+/.test(normalized);
  const looksLikePhone = /(?:\+?\d[\d\s().-]{6,}\d)/.test(normalized);

  return normalized && !looksLikeEmail && !looksLikePhone
    ? normalized
    : undefined;
};

export const trackGa4Search = ({
  searchTerm,
  resultCount,
  resultTypes,
  surface,
}: {
  searchTerm: string;
  resultCount: number;
  resultTypes: string[];
  surface: string;
}) => {
  const safeSearchTerm = getSafeSearchTerm(searchTerm);

  return trackGa4Event('search', {
    ...(safeSearchTerm ? { search_term: safeSearchTerm } : {}),
    search_surface: surface,
    result_count: resultCount,
    result_types: resultTypes.join(','),
    has_results: resultCount > 0,
  });
};

export const trackGa4ContentSelection = ({
  contentType,
  contentId,
  sectionTitle,
  surface,
  params = {},
}: {
  contentType: string;
  contentId: string;
  sectionTitle?: string;
  surface: string;
  params?: Record<string, unknown>;
}) =>
  trackGa4Event('select_content', {
    ...params,
    content_type: contentType,
    content_id: contentId,
    ...(sectionTitle ? { content_section: sectionTitle } : {}),
    content_surface: surface,
  });

export const normalizeSportCategory = (slug?: string | null) => {
  const normalized = slug?.trim().toLowerCase() || '';

  if (normalized.includes('ncaa') && normalized.includes('football')) {
    return 'NCAA Football';
  }
  if (normalized.includes('concert') || normalized.includes('music')) {
    return 'Concert';
  }

  const knownCategories: Record<string, string> = {
    mlb: 'MLB',
    'mlb-spring-training': 'MLB Spring Training',
    nba: 'NBA',
    nfl: 'NFL',
    nhl: 'NHL',
    mls: 'MLS',
    'mls-major-league-soccer': 'MLS',
    wnba: 'WNBA',
    pga: 'PGA',
    'ncaa-mens-basketball': 'NCAA Basketball',
    'ncaa-mens-football': 'NCAA Football',
  };

  return knownCategories[normalized] || 'Other';
};

export const buildBookingConversion = ({
  orderId,
  eventId,
  eventName,
  value,
  currency,
  league,
  packageType,
  failureStages = [],
  items,
}: {
  orderId: string | number | null | undefined;
  eventId: string | number | null | undefined;
  eventName: string;
  value: number;
  currency: string;
  league: string;
  packageType: 'package' | 'tickets_only';
  failureStages?: string[];
  items?: Ga4Item[];
}): BookingConversion | null => {
  const transactionId = orderId == null ? '' : String(orderId).trim();
  const normalizedEventId = eventId == null ? '' : String(eventId).trim();
  const normalizedEventName = eventName.trim();
  const normalizedCurrency = currency.toUpperCase();
  const normalizedLeague = league.trim() || 'other';
  const normalizedValue = Math.round(Number(value) * 100) / 100;

  if (
    !transactionId ||
    !normalizedEventId ||
    !normalizedEventName ||
    !Number.isFinite(normalizedValue) ||
    normalizedValue <= 0 ||
    (normalizedCurrency !== 'CAD' && normalizedCurrency !== 'USD')
  ) {
    return null;
  }

  const uniqueFailureStages = [
    ...new Set(failureStages.filter(Boolean)),
  ].sort();
  const isPartial = uniqueFailureStages.length > 0;
  const conversionItems = items?.length
    ? items
    : [
        {
          item_id: normalizedEventId,
          item_name: normalizedEventName,
          item_category: normalizeSportCategory(normalizedLeague),
          item_category2: normalizedLeague,
          item_category3: packageType,
          price: normalizedValue,
          quantity: 1,
        },
      ];

  return {
    eventName: isPartial ? 'paid_partial_booking' : 'purchase',
    transactionId,
    params: {
      transaction_id: transactionId,
      value: normalizedValue,
      currency: normalizedCurrency,
      ...(isPartial && { failure_stage: uniqueFailureStages.join(',') }),
      items: conversionItems,
    },
  };
};

export const savePendingConversion = (
  conversion: BookingConversion,
  now = Date.now()
) => {
  const conversions = readConversions(now);
  conversions[conversion.transactionId] = {
    ...conversion,
    expiresAt: now + PENDING_CONVERSION_TTL_MS,
  };

  return writeConversions(conversions);
};

export const trackPendingConversion = (
  transactionId: string | number,
  now = Date.now()
) => {
  const normalizedTransactionId = String(transactionId).trim();
  if (!normalizedTransactionId) return false;

  const conversions = readConversions(now);
  const conversion = conversions[normalizedTransactionId];

  if (!conversion || conversion.sentAt) return false;

  const tracked = trackGa4Event(conversion.eventName, conversion.params);
  if (!tracked) return false;

  conversions[normalizedTransactionId] = {
    ...conversion,
    sentAt: now,
  };
  writeConversions(conversions);
  return true;
};
